/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { getBoxShadowStyle } from '@styles/shadow';
import { SmallLoading } from '@components/loading';
import Svg from '@styles/icons/svg';

import TouchableElement from '@components/common/touchableElement';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { SimpleOnPress } from '@interfaces/base';
import { ColorFunction } from '@styles/colors/types';

const LineProgressButton = ({
  C,
  progress,
  onPress,
  height = 42,
  loading,
  borderBottom = false,
  shadow = true,
  borderRadiusTop = true,
  borderRadiusBottom = true,
  bg = 'Background / Light / Field',
  fontTitle: customFontTitle,
  fontText: customFontText,
  title,
  text,
  status,
  additionalBtnIcon,
  additionalBtnColor,
  onAdditionalBtnPress,
  scale = initialScale,
  font = initialFont, 
}: Partial<SimpleOnPress> & {
  C: ColorFunction;
  progress?: number;
  height?: number;
  loading?: boolean;
  borderBottom?: boolean;
  shadow?: boolean;
  borderRadiusTop?: boolean;
  borderRadiusBottom?: boolean;
  bg?: string;
  fontTitle?: string;
  fontText?: string;
  title?: string;
  text?: string | null;
  status: 'running' | 'skipped' | 'completed' | 'notStartReady' | 'readyToStart';
  additionalBtnIcon?: string;
  additionalBtnColor?: string;
  onAdditionalBtnPress?: () => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}) => {
  const statusIcon = () => {
    switch (status) {
      case 'running':
        return 'runningMan';
      case 'skipped':
        return 'fastForward';
      case 'completed':
        return 'checkboxGrey';
      case 'notStartReady':
        return 'hourglass';
      case 'readyToStart':
        return 'readyToRunMan';
    }
  };

  const statusText = () => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'skipped':
        return 'Skipped';
      case 'completed':
        return 'Completed';
      case 'notStartReady':
        return 'Not Start Ready';
      case 'readyToStart':
        return 'Ready to Start';
    }
  };

  return (
    <View
      style={[
        { height: scale(height), userSelect: 'none' },
        shadow && {
          borderRadius: scale(SCREEN_BORDER_RADIUS),
        },
        shadow && getBoxShadowStyle({type: 'Field', C}),
        borderBottom && {
          borderBottomColor: C('Hairline/Light/No trans'),
          borderBottomWidth: StyleSheet.hairlineWidth,
        },
      ]}
    >
      <TouchableElement onPress={onPress} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}>
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              {
                backgroundColor: C(pressHoverColor(bg)),
                height: '100%',
                width: '100%',
                justifyContent: 'center',
                alignContent: 'center',
              },
              borderRadiusTop && {
                borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
              },
              borderRadiusBottom && {
                borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
              },
              boxStyle,
            ]}
          >
            {loading ? (
              <SmallLoading />
            ) : (
              <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: scale(15), gap: scale(5) }}>
                <View style={{ paddingVertical: scale(8), width: '30%' }}>
                  <Text numberOfLines={1} style={[font[customFontTitle || 'Headline'], { color: C('Label/Light/Primary') }]}>
                    {title}
                  </Text>
                  {text && (
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Svg icon="finishFlag" width={scale(13)} height={scale(16)} color={'Label/Light/Tertiary'} />
                      <Text
                        numberOfLines={1}
                        style={[font[customFontText || 'Subhead 2'], { color: C('Label/Light/Secondary'), marginLeft: scale(5) }]}
                      >
                        {text}
                      </Text>
                    </View>
                  )}
                </View>
                <View
                  style={{
                    paddingBottom: scale(8),
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    alignItems: 'center',
                    height: scale(8),
                    width: '30%',
                    backgroundColor: C('Label/Light/Tertiary'),
                    borderRadius: scale(5),
                    overflow: 'hidden',
                  }}
                >
                  <View
                    style={{
                      height: scale(8),
                      backgroundColor: C('Status/Light/Active'),
                      borderRadius: scale(5),
                      marginRight: 'auto',
                      width: progress ? `${progress}%` : 0,
                    }}
                  />
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: scale(5),
                    width: '30%',
                    marginLeft: scale(10),
                  }}
                >
                  <Text numberOfLines={1} style={[font[customFontText || 'Footnote'], { color: C('Label/Light/Secondary'), textAlign: 'center' }]}>
                    {i18n.t(statusText())}
                  </Text>
                  <Svg icon={statusIcon()} width={20} height={20} />
                  {onAdditionalBtnPress && additionalBtnIcon ? (
                    <TouchableElement onPress={onAdditionalBtnPress}>
                      {({ boxStyle }) => (
                        <View style={[boxStyle]}>
                          <Svg icon={additionalBtnIcon} color={additionalBtnColor} height={17} width={16} />
                        </View>
                      )}
                    </TouchableElement>
                  ) : (
                    <Svg icon="chevronRightLight" height={scale(11)} width={scale(7)} color={'Label / Light / Secondary'} />
                  )}
                </View>
              </View>
            )}
          </View>
        )}
      </TouchableElement>
    </View>
  );
};

export default LineProgressButton;
